/**
 * In-memory patient context (HIPAA: avoid persisting names/diagnosis in localStorage).
 * patientId / hostPatientId still sync to sessionStorage for legacy Rx pages until fully migrated.
 */

const NULL_LITERAL = "null";

type PatientContext = {
  patientId: string;
  hostPatientId: string;
  patientName: string;
  diagnosis: string;
};

const memory: PatientContext = {
  patientId: "",
  hostPatientId: "",
  patientName: "",
  diagnosis: "",
};

function normalizeId(value: string | null | undefined): string {
  const v = (value ?? "").trim();
  if (!v || v.toLowerCase() === NULL_LITERAL) {
    return "";
  }
  return v;
}

function normalizeName(value: string | null | undefined): string {
  const v = (value ?? "").trim();
  if (!v || v.toLowerCase() === NULL_LITERAL) {
    return "";
  }
  return v;
}

/** Remove legacy PHI keys from browser storage (call on logout and app init). */
export function clearPhiFromBrowserStorage(): void {
  memory.patientId = "";
  memory.hostPatientId = "";
  memory.patientName = "";
  memory.diagnosis = "";

  if (typeof sessionStorage !== "undefined") {
    sessionStorage.removeItem("patientName");
    sessionStorage.removeItem("currentPatientName");
    sessionStorage.removeItem("aiPrescriptionSearchHistory");
    sessionStorage.removeItem("rxTabs");
    sessionStorage.removeItem("rxTabsErrors");
    sessionStorage.removeItem("freeTextRxTabs");
    sessionStorage.removeItem("freeTextRxTabsErrors");
    sessionStorage.removeItem("locationData");
    sessionStorage.removeItem("rxHubOptions");
    sessionStorage.removeItem("rxhubPBMUniqueId");
    sessionStorage.removeItem("pdmpPopup");
    sessionStorage.removeItem("TabValue");
  }
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem("patientName");
    localStorage.removeItem("diagnosis");
  }
}

/** Load patient id from legacy sessionStorage into memory; strip stored display name. */
export function migrateLegacyPhiStorage(): void {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  const legacyId = normalizeId(sessionStorage.getItem("patientId"));
  const legacyHost = normalizeId(sessionStorage.getItem("hostPatientId"));
  if (legacyId && !memory.patientId) {
    memory.patientId = legacyId;
  }
  if (legacyHost && !memory.hostPatientId) {
    memory.hostPatientId = legacyHost;
  }
  sessionStorage.removeItem("patientName");
  sessionStorage.removeItem("currentPatientName");
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem("patientName");
    const legacyDx = localStorage.getItem("diagnosis");
    if (legacyDx && legacyDx.toLowerCase() !== NULL_LITERAL && !memory.diagnosis) {
      memory.diagnosis = legacyDx.trim();
    }
    localStorage.removeItem("diagnosis");
  }
}

export function getPatientId(): string {
  if (memory.patientId) {
    return memory.patientId;
  }
  if (typeof sessionStorage !== "undefined") {
    return normalizeId(sessionStorage.getItem("patientId"));
  }
  return "";
}

export function setPatientId(patientId: string | null | undefined): void {
  const v = normalizeId(patientId ?? "");
  memory.patientId = v;
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem("patientId", v || NULL_LITERAL);
  }
}

export function getHostPatientId(): string {
  if (memory.hostPatientId) {
    return memory.hostPatientId;
  }
  if (typeof sessionStorage !== "undefined") {
    return normalizeId(sessionStorage.getItem("hostPatientId"));
  }
  return "";
}

export function setHostPatientId(hostPatientId: string | null | undefined): void {
  const v = normalizeId(hostPatientId ?? "");
  memory.hostPatientId = v;
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem("hostPatientId", v || NULL_LITERAL);
  }
}

/** Display name — memory only (not written to sessionStorage/localStorage). */
export function getPatientName(): string {
  return memory.patientName;
}

export function setPatientName(name: string | null | undefined): void {
  memory.patientName = normalizeName(name ?? "");
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.removeItem("patientName");
    sessionStorage.removeItem("currentPatientName");
  }
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem("patientName");
  }
}

export function getDiagnosis(): string {
  return memory.diagnosis;
}

export function setDiagnosis(diagnosis: string | null | undefined): void {
  const v = (diagnosis ?? "").trim();
  memory.diagnosis = v && v.toLowerCase() !== NULL_LITERAL ? v : "";
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem("diagnosis");
  }
}

export function setPatientContext(ctx: {
  patientId?: string | null;
  hostPatientId?: string | null;
  patientName?: string | null;
  diagnosis?: string | null;
}): void {
  if (ctx.patientId !== undefined) {
    setPatientId(ctx.patientId);
  }
  if (ctx.hostPatientId !== undefined) {
    setHostPatientId(ctx.hostPatientId);
  }
  if (ctx.patientName !== undefined) {
    setPatientName(ctx.patientName);
  }
  if (ctx.diagnosis !== undefined) {
    setDiagnosis(ctx.diagnosis);
  }
}
